import { type ServerResponse, axios } from '~/axios'

interface FamilyMember {
  memberIdentifier: string
  firstParticipant: boolean
  firstName: string
  lastName: string
  gender: string
  genderText: string
  customGender: string | null
  birthDate: number | null
  documentType: string
  documentTypeText: string
  customDocumentType: string | null
  documentNumber: string
  birthCountry: string
  email: string | null
  phoneNumber: string | null
  phoneCode: string | null
  socioEconomicAnswers: Array<{ key: string; value: string }>
}

interface FamilyData {
  name: string
  latitude: string
  longitude: string
  familyMembersList: FamilyMember[]
}

interface SnapshotEconomic {
  codeName: string
  value: string | null
  multipleValueArray: string[] | null
  questionText: string
  text: string | null
  multipleText: string[] | null
  multipleTextArray: string[] | null
  other: string | null
  topic: string
}

interface MemberEconomic {
  memberIdentifier: string
  firstName: string
  economic: SnapshotEconomic[]
}

interface Stoplight {
  codeName: string
  value: number
  shortName: string
  lifemapName: string
  priority: null | boolean
  achievement: null | boolean
}

export interface Snapshot {
  snapshotId: number
  surveyDefinition: { id: number; title: string; lang: string }
  snapshotDate: number
  isLast: boolean
  stoplightDate: null | number
  stoplightSkipped: boolean
  surveyUser: string
  projectTitle: string
  projectId: null | number
  stoplight: Stoplight[]
  priorities: Array<{ indicator: string }> | null
  achievements: Array<{ indicator: string }> | null
  familyData: FamilyData
  economic: SnapshotEconomic[]
  membersEconomic: MemberEconomic[]
}

/**
 * Fetches all snapshots for a family
 * @param familyId - The numeric ID of the family
 * @returns Array of snapshots
 */
export async function getSnapshotsByFamilyId(
  familyId: number,
): Promise<Snapshot[]> {
  const query = /* GraphQL */ `
    query getSnapshotsByFamily($familyId: Long!) {
      familySnapshotsOverview(familyId: $familyId) {
        snapshots {
          snapshotId
          surveyDefinition {
            id
            title
            lang
          }
          snapshotDate
          isLast
          stoplightDate
          stoplightSkipped
          surveyUser
          projectTitle
          projectId
          stoplight {
            codeName
            value
            shortName
            lifemapName
            priority
            achievement
          }
          priorities {
            indicator
          }
          achievements {
            indicator
          }
          familyData {
            name
            latitude
            longitude
            familyMembersList {
              memberIdentifier
              firstParticipant
              firstName
              lastName
              gender
              genderText
              customGender
              birthDate
              documentType
              documentTypeText
              customDocumentType
              documentNumber
              birthCountry
              email
              phoneNumber
              phoneCode
              socioEconomicAnswers {
                key
                value
              }
            }
          }
          economic {
            codeName
            value
            multipleValueArray
            questionText
            text
            multipleText
            multipleTextArray
            other
            topic
          }
          membersEconomic {
            memberIdentifier
            firstName
            economic {
              codeName
              value
              multipleValue
              multipleValueArray
              questionText
              text
              multipleText
              multipleTextArray
              other
              topic
            }
          }
        }
      }
    }
  `

  const { data } = await axios.post<
    ServerResponse<{ familySnapshotsOverview: { snapshots: Snapshot[] } }>
  >('/graphql', {
    query,
    variables: { familyId },
  })

  return data.data.familySnapshotsOverview.snapshots
}
